import { useMemo, useState } from 'react';
import { PRESET_CATEGORIES, TEXTURE_PACK, type TexturePresetCategory } from '@/core/texture/pack';
import { useDesignStore } from '@/state/store';
import { Thumbnail } from '../components/Thumbnail';

/**
 * The Core Texture Pack browser.
 *
 * Thumbnails render two panels across so a motif that crosses a joint reads
 * correctly on the card — several presets only make sense at that scale.
 */
export function PackTab() {
  const design = useDesignStore((state) => state.design);
  const applyPreset = useDesignStore((state) => state.applyPreset);
  const [category, setCategory] = useState<TexturePresetCategory | 'all'>('all');

  const presets = useMemo(
    () => (category === 'all' ? TEXTURE_PACK : TEXTURE_PACK.filter((p) => p.category === category)),
    [category],
  );

  const activeId = design.source.kind === 'pack' ? design.source.presetId : undefined;

  return (
    <div className="pack">
      <div className="chips">
        <button
          type="button"
          className={`chip${category === 'all' ? ' is-active' : ''}`}
          onClick={() => setCategory('all')}
        >
          All
        </button>
        {PRESET_CATEGORIES.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className={`chip${category === entry.id ? ' is-active' : ''}`}
            onClick={() => setCategory(entry.id)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <div className="preset-grid">
        {presets.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className={`preset-card${activeId === preset.id ? ' is-active' : ''}`}
            onClick={() => applyPreset(preset.id)}
            title={preset.description}
          >
            <Thumbnail
              layers={preset.build()}
              finish={{ ...design.finish, ...preset.finish }}
              alt={preset.name}
            />
            <span className="preset-card__name">{preset.name}</span>
            <span className="preset-card__meta">{preset.suggestedDepth} mm</span>
          </button>
        ))}
      </div>

      <p className="hint">
        Presets set the layers and a matching relief depth. Everything stays editable — open{' '}
        <strong>Layers</strong> to take one apart.
      </p>
    </div>
  );
}
